import React, { useState, useEffect, useCallback, useRef } from 'react';
import mixpanel from 'mixpanel-browser';
import lodash, { toLength } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Loader } from '@googlemaps/js-api-loader';

import { TextField, CircularProgress, Autocomplete, IconButton, Divider } from '@mui/material';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import PinDropIcon from '@mui/icons-material/PinDrop';
import HistoryIcon from '@mui/icons-material/History';
import ClearIcon from '@mui/icons-material/Clear';
import DrawIcon from '@mui/icons-material/Draw';
import MapIcon from '@mui/icons-material/Map';

import { callApi } from 'src/core/callApi';
import {
  COLOR_PRIMARY_LIGHT,
  COLOR_PRIMARY,

  NOT_VERIFIED_USER_ALLOWED_INVESTMENT_ESTIMATOR_SEARCH_ATTEMPTS,
} from 'src/const';
import {
  returnLoadingText,
  makeLowerCase,
  geocodeAddress,
  checkIfGooglePlacesSessionValid,
} from 'src/utils/functions';
import { CachedSearchedMarketsType, DashboardState, SearchObject } from 'src/interfaces/idashboard';
import { SearchBarPropTypes } from 'src/interfaces/iproptypes';
import { setSearchLimitAlertBox } from 'src/store/Dashboard/actions';
import { VerifiedTag } from '../../Tags';
import MarketSearch from 'src/core/MarketSearch';
import {
  COLOR_BACKGROUND_2,
  COLOR_TEXT_1,
  COLOR_TEXT_4,
  COLOR_TEXT_3,
  DEFAULT_TEXT_COLOR,
  COLOR_GREY_600,
  COLOR_PRIMARY_2
} from 'src/colors';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    options: {
      '&:last-child': {
        paddingBottom: '40px',
      },
      '&:hover': {
        backgroundColor: COLOR_PRIMARY_LIGHT,
        fontColor: '#abcdef',
        textColor: '#abcdef',
      },
    },
    singleFixedOption: {
      position: 'absolute',
      top: 5,
      left: 0,
      width: '100%',
      zIndex: 1,
      borderBottomLeftRadius: '4px',
      borderBottomRightRadius: '4px',
      paddingLeft: '10px',
      fontSize: '12px',
      backgroundColor: 'white',
      boxShadow:
        '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
      '&:hover': {
        backgroundColor: COLOR_PRIMARY_LIGHT,
        fontColor: '#abcdef',
        textColor: '#abcdef',
      },
    },
    fixedOption: {
      position: 'absolute',
      bottom: -20,
      left: 0,
      width: '98%',
      backgroundColor: 'white',
      zIndex: 1,
      borderBottomLeftRadius: '4px',
      borderBottomRightRadius: '4px',
      paddingLeft: '10px',
      fontSize: '12px',
      '&:hover': {
        backgroundColor: COLOR_PRIMARY_LIGHT,
        fontColor: '#abcdef',
        textColor: '#abcdef',
      },
    },
  })
);

const SearchTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  '& div': {
    filter: 'drop-shadow(0 0 5px #ccc)',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#ebfcff',
    marginBottom: -10,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#ebfcff',
    border: 'none',
    padding: '10px 20px',
    fontSize: '16px',
    color: '#037a90',
    fontWeight: 'normal',
  },
}));

const compareResults = (result1: SearchObject, result2: SearchObject) => {
  // Compare by priority
  const priorityComparison = result1.priority.localeCompare(result2.priority);

  // If priorities are the same, compare by isVerified
  if (priorityComparison === 0) {
    // +true = 1, +false = 0
    const verifiedComparison = +result2.isVerified - +result1.isVerified;

    // If isVerified is the same, compare by lexical order of searchName
    return verifiedComparison === 0
      ? result1.searchName.localeCompare(result2.searchName)
      : verifiedComparison;
  }

  return priorityComparison;
};

const sortSearchResults = (searchResults: SearchObject[], inputText: string | undefined) => {
  if (!inputText) {
    return searchResults;
  }

  let exactMatches: SearchObject[] = [];
  let partialMatches: SearchObject[] = [];
  let nonMatches: SearchObject[] = [];

  const seenSearchNames = new Set<string>();

  for (const searchResult of searchResults) {
    if (seenSearchNames.has(searchResult.searchName)) continue;

    if (
      searchResult.searchName.slice(0, inputText.length).toLowerCase() == inputText.toLowerCase()
    ) {
      exactMatches.push(searchResult);
    } else if (searchResult.searchName.search(new RegExp(inputText, 'ig')) > -1) {
      partialMatches.push(searchResult);
    } else {
      nonMatches.push(searchResult);
    }
    seenSearchNames.add(searchResult.searchName);
  }

  exactMatches = exactMatches.sort(compareResults);
  partialMatches = partialMatches.sort(compareResults);
  nonMatches = nonMatches.sort(compareResults);

  console.log('========================\n', {
    'exact search matches': exactMatches.length,
    'semi search matches': partialMatches.length,
    'not search matches': nonMatches.length,
  });

  return [...exactMatches, ...partialMatches, ...nonMatches];
};

const reArrangeOptions = (
  options: SearchObject[],
  inputRef: React.MutableRefObject<HTMLInputElement | undefined>
) => {
  let reArrangedOptions = [] as SearchObject[];
  const fastSearchOptions = options.filter(option => option.isFastSearchResult);
  const savedRegions = options.filter(option => option.isSavedRegion);
  const lambdaOptions = options.filter(option => option.isSearchLambdaResult);
  const isDefaultOption = options.filter(option => option.isDefaultOption);
  reArrangedOptions =
    inputRef?.current?.value == ''
      ? [...savedRegions]
      : fastSearchOptions.length || lambdaOptions.length
      ? // ? sortSearchResults([...fastSearchOptions, ...lambdaOptions], inputRef?.current?.value)
        [...lambdaOptions, ...fastSearchOptions]
      : isDefaultOption;

  // makeBetterSort(reArrangedOptions, inputRef?.current?.value);

  return [...reArrangedOptions, ...isDefaultOption];
};

const SEARCH_MARKET_PLACEHOLDER = 'Search for a market ...';
const TAILORED_REGION_PLACEHOLDER = 'Draw your Custom Market';

const Asynchronous = ({
  search,
  showAlert,
  notShowAlert,
  setdashboardState,
  matches,
  user,
  district,
  isUserVerified,
  rateLimitAlert,
  setRateLimitAlert,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  isOnboardingOn,
  isMobileScreen,
  cachedSearchMarkets,
  appTourStep,
  closeMobileSearch,
}: SearchBarPropTypes) => {
  const history = useHistory();

  const classes = useStyles();
  const dispatch = useDispatch();
  // const loader = new Loader({
  //   apiKey: 'AIzaSyDUnmEGqix9gziZQjtCQFJ7mCTSDV9yPDk',
  //   id: '__googleMapsScriptId',
  //   version: 'weekly',
  //   libraries: ['drawing', 'places', 'visualization'],
  // });

  const [open, setOpen] = useState<boolean>(false);
  const [placeholderText, setPlaceholderText] = useState<string>(SEARCH_MARKET_PLACEHOLDER);
  const [options, setOptions] = useState<SearchObject[]>([] as SearchObject[]);

  const [searchBarState, setSearchBarState] = useState<string>('');
  const [loadingState, setLoadingState] = useState<boolean>(false);

  const searchLimitAlertBox = useSelector(state => (state as any).dashboard.searchLimitAlertBox);

  const [isSearchBarActive, setIsSearchBarActive] = useState<boolean>(false);

  const [marketSearch, setMarketSearch] = useState<MarketSearch | null>(null);
  const searchTooltipRef = useRef<any>();
  const inputRef = useRef<HTMLInputElement>();

  const loading = open && loadingState;
  const emptySearchBar = open && searchBarState.length === 0;

  // useEffect(() => {
  //  setTimeout(()=>{
  //   if (appTourStep == 0 && !matches) {
  //     setOpen(openDefaultValue);
  //     setOptions(optionsDefaultValue);
  //   }
  //   if (appTourStep != 0) {
  //     setOpen(false);
  //     setOptions([] as SearchObject[]);
  //   }},100)
  // },[appTourStep]);

  useEffect(() => {
    setPlaceholderText(
      window.location.pathname.includes('tailored')
        ? TAILORED_REGION_PLACEHOLDER
        : SEARCH_MARKET_PLACEHOLDER
    );
  }, [search, district]);

  useEffect(() => {
    const initizeSearch = async () => {
      const marketSearch = new MarketSearch(cachedSearchMarkets);
      setMarketSearch(marketSearch);
    };

    cachedSearchMarkets && initizeSearch();
  }, [cachedSearchMarkets]);

  const getRedirectionLink = (
    prev: DashboardState,
    countryParam: string = '',
    cityParam: string = '',
    districtParam: string = '',
    coordinateParam: string = '',
    isGoogleAddress: boolean = false
  ) => {
    let paramStr = '';
    if (isGoogleAddress) {
      // paramStr = `${coordinateParam}/${districtParam}`;
      const [lat, lng] = coordinateParam
        .replace('(', '')
        .replace(')', '')
        .split(',');
      return `/tailored/t?lat=${lat}&lng=${lng}`;
    } else {
      paramStr = `${countryParam}/${cityParam}/${districtParam}`;
    }
    if (prev.showGuestsDashboard) {
      return isGoogleAddress
        ? `/airbnb-data/${paramStr}?guests=fetching`
        : `/airbnb-data/${paramStr}?guests=true`;
    } else {
      return `/airbnb-data/${paramStr}`;
    }
  };

  const isSearchAllowed = () => {
    const search_attempt_history = localStorage.getItem('investment-search-attempts');
    const noOfUserSearch = search_attempt_history ? parseInt(search_attempt_history) : 0;

    if (
      !isUserVerified &&
      noOfUserSearch >= NOT_VERIFIED_USER_ALLOWED_INVESTMENT_ESTIMATOR_SEARCH_ATTEMPTS
    ) {
      return false;
    }

    return true;
  };

  const handleSearchChange = useCallback(
    async (_, values) => {
      const search_attempt_history = localStorage.getItem('investment-search-attempts');
      const noOfUserSearch = search_attempt_history ? parseInt(search_attempt_history) : 0;

      if (!isSearchAllowed()) {
        setOpen(false);
        setRateLimitAlert(true);
        return;
      }

      if (!values) {
        return;
      }

      if (values.isDefaultOption) {
        history.push('/tailored/t');
        return;
      }

      if (values.isGoogleAPIResult) {
        const [lat, lng] = (await geocodeAddress(values.name)) as [number, number];
        const coordinate = `(${lat},${lng})`;

        setdashboardState(prev => {
          const redirectTo = getRedirectionLink(prev, '', '', values.name, coordinate, true);
          localStorage.setItem('investment-search-attempts', (noOfUserSearch + 1).toString());
          window.history.pushState({}, '', redirectTo);
          window.location.reload();
          return prev;
        });
        return;
      }

      if (values.redirectToTailoredRegion) {
        let tailoredAddress: string = `/tailored/t?lat=${values.lat}&lng=${values.lng}`;
        if (values.name) {
          tailoredAddress += '&name=' + values.name;
        }
        history.push(tailoredAddress);
        return;
      }

      let countryParam: string = values.country
        ? makeLowerCase(values.country).replace(/ /g, '-')
        : '0';
      let cityParam: string = values.city ? makeLowerCase(values.city) : '0';
      let districtParam: string = makeLowerCase(values.name);
      setdashboardState(prev => {
        const redirectTo = getRedirectionLink(prev, countryParam, cityParam, districtParam);
        localStorage.setItem('investment-search-attempts', (noOfUserSearch + 1).toString());
        window.history.pushState({}, '', redirectTo);
        window.location.reload();
        return prev;
      });
    },
    [isUserVerified]
  );

  const lastPromise = useRef();

  const formatSearchBarListingNames = (district: string, city: string, country: string): string => {
    if (!district) return '';

    let result = district;
    if (city && city != '0') {
      result += ', ' + city;
    }
    result += ` ( ${country} )`;
    return result;
  };

  const handleInputChangeFastSearch = useCallback(
    lodash.debounce((values, savedRegions) => {
      mixpanel.track('searchDistrict', {
        searchVal: values,
      });

      dispatch(setSearchLimitAlertBox(false));

      if (values.length > 0) {
        setSearchBarState(values);
        setLoadingState(true);

        try {
          let searchObjs: SearchObject[] = [];

          //===========================================
          // FAST SEARCH
          //

          const fastSearchResults: CachedSearchedMarketsType[] = marketSearch
            ? marketSearch.calculate(values)
            : [];

          fastSearchResults.forEach(market => {
            searchObjs.push({
              // searchName: `${market.district}, ${market.city} ( ${market.country} )`,
              searchName: formatSearchBarListingNames(market.district, market.city, market.country),
              name: market.district,
              city: market.city,
              country: market.country,
              priority: market.priority,
              isSearchLambdaResult: false,
              isVerified: market.isVerified,
              isFastSearchResult: true,
            });
          });

          searchObjs.push({
            searchName: values,
            name: 'TailoredRegion',
            isDefaultOption: true,
            isVerified: false,
            priority: 'A',
          });

          setOptions(searchObjs);
        } catch (err) {
          console.log('Error fast search: ', err);
        }
      } else {
        setLoadingState(false);
        showPreviouslySubscribedMarkets(savedRegions);
      }
    }, 300),
    [marketSearch, inputRef.current]
  );

  const showPreviouslySubscribedMarkets = savedRegions => {
    let searchObjs: SearchObject[] = [];

    savedRegions &&
      [...savedRegions].reverse().forEach(region => {
        if (typeof region === 'string') {
          searchObjs.push({
            searchName: region,
            name: region,
            isSavedRegion: true,
            isVerified: false,
            priority: 'A',
          });
        } else {
          searchObjs.push({
            searchName: region.region,
            name: region.region,
            city: region.city,
            country: region.country,
            isSavedRegion: true,
            isVerified: false,
            priority: 'A',
          });
        }
      });

    setOptions(prevOptions => [...prevOptions, ...searchObjs]);
  };

  const handleInputChangeCallAPI = useCallback(
    lodash.debounce(async (event, values, lastPromise) => {
      if (values.length > 0 && values.trim() !== '') {
        let apiTest =
          ' https://wnwvnwko88.execute-api.us-east-2.amazonaws.com/default/searchAutoCompleteTest';

        let api: string =
          'https://zbg6tnxjk1.execute-api.us-east-2.amazonaws.com/default/searchAutoComplete';

        try {
          let searchObjs: SearchObject[] = [];

          const currentPromise = callApi(
            // apiTest,
            api,
            {
              queryParams: {
                prefix: values,
              },
            },
            true,
            false
          ).then(res => {
            return res;
          });
          lastPromise.current = currentPromise;

          currentPromise.then(
            response => {
              if (
                currentPromise === lastPromise.current &&
                response.message.constructor === Array
              ) {
                setLoadingState(false);
                if (response && response.message) {
                  if (response.message.length === 0) {
                    showAlert();
                  } else {
                    notShowAlert();
                  }

                  if (response.redirectToTailoredRegion) {
                    const { lat, lng } = response.coordinates;
                    searchObjs.push({
                      searchName: values + ' (select area from map)',
                      name: values,
                      lat,
                      lng,
                      redirectToTailoredRegion: true,
                      isVerified: false,
                      priority: 'A',
                    });
                  }

                  response.message.forEach(function(item, index) {
                    let districtName: string = item[0];
                    let cityName: string = item[5];
                    let countryName: string = item[6];
                    let priority: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' = item[7];

                    searchObjs.push({
                      searchName: formatSearchBarListingNames(districtName, cityName, countryName),
                      name: item[0],
                      lat: item[2],
                      lng: item[1],
                      polygon: '',
                      multiPolygon: '',
                      city: item[5],
                      country: item[6],
                      countryCode: item[8],
                      bounds: item[9],
                      isSearchLambdaResult: true,
                      isVerified: item[10],
                      priority: priority,
                    });
                  });
                }

                // setOptions([]); // to reinitialize the options
                if (inputRef.current?.value !== '' && inputRef.current?.value.trim() !== '') {
                  setOptions(prev => [...prev, ...searchObjs]);
                }
              }
            },
            e => {
              if (currentPromise === lastPromise.current) {
                console.warn('fetch failure', e);
              }
            }
          );
        } catch (error) {
          console.error((error as any)?.message);
        }
      }
    }, 600),
    [marketSearch, inputRef.current]
  );

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  const restorePlaceholderText = () =>
    setPlaceholderText(
      window.location.pathname.includes('tailored')
        ? TAILORED_REGION_PLACEHOLDER
        : SEARCH_MARKET_PLACEHOLDER
    );

  const removePlaceholderText = () => setPlaceholderText('');

  const clearSearchBar = () => {
    setTimeout(() => {
      if (inputRef.current) {
        placeholderText == '' && inputRef.current.blur();
        inputRef.current.value = '';
      }
    }, 10);
    setSearchBarState('');
    setOptions([]);
    setOpen(false);
    closeMobileSearch && closeMobileSearch();
  };

  const StartAdorement = () => {
    return (
      <>
        {/* search icon */}
        {isOnboardingOn === true ? (
          <SearchTwoToneIcon
            sx={{
              color: '#fff',
              background: COLOR_PRIMARY_2,
              padding: '8px 16px',
              borderRadius: 12,
            }}
          />
        ) : (
          <SearchTwoToneIcon sx={{ color: COLOR_PRIMARY_2, paddingLeft: 1, marginRight: 0 }} />
        )}
      </>
    );
  };

  const EndAdorement = () => {
    return (
      <>
        {/* loading animation */}
        {!emptySearchBar && loading ? (
          <CircularProgress sx={{ color: COLOR_PRIMARY }} size={20} />
        ) : null}
        {/* clear button */}
        <IconButton sx={{ color: DEFAULT_TEXT_COLOR, paddingRight: 1 }} onClick={clearSearchBar}>
          <ClearIcon />
        </IconButton>
        {!isSearchBarActive && (
          <>
            {/* divider  */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{ margin: '2px 10px', borderRightWidth: 3 }}
            />
            {/* custom market */}
            <Tooltip title="Draw Custom Market">
              <IconButton
                sx={{ color: COLOR_TEXT_4, paddingRight: 1 }}
                onClick={() => {
                  history.push('/tailored/t');
                  return;
                }}
              >
                <DrawIcon />
              </IconButton>
            </Tooltip>
            {/* discovery */}
            <Tooltip
              arrow
              title="Discover high ROI Markets"
            >
              <IconButton
                sx={{ color: COLOR_TEXT_4, paddingRight: 1, marginRight: '20px' }}
                onClick={() => {
                  localStorage.setItem('openDiscovery', 'true');
                  history.push('/airbnb-data');
                  return;
                }}
              >
                <MapIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <SearchTooltip
        title="Search your location here ..."
        placement="top"
        arrow
        open={searchLimitAlertBox}
      >
        <Autocomplete
          ref={searchTooltipRef}
          id="autocomplete"
          open={open}
          classes={{
            option: classes.options,
          }}
          onOpen={() => {
            setOpen(true);
            setOptions(e => {
              let tempSearchObjs: SearchObject[] = [];
              user.regions &&
                [...user.regions].reverse().forEach((region: any) => {
                  if (typeof region === 'string') {
                    tempSearchObjs.push({
                      searchName: region,
                      name: region,
                      isSavedRegion: true,
                      isVerified: false,
                      priority: 'A',
                    });
                  } else {
                    tempSearchObjs.push({
                      searchName: region.region,
                      name: region.region,
                      city: region.city,
                      country: region.country,
                      isSavedRegion: true,
                      isVerified: false,
                      priority: 'A',
                    });
                  }
                });
              return tempSearchObjs;
            });
          }}
          onClose={() => {
            setOpen(false);
          }}
          options={reArrangeOptions(options, inputRef)}
          filterOptions={(options, params) => {
            return options;
          }}
          loading={loading}
          onInputChange={(event, value) => {
            setOptions([]);
            handleInputChangeFastSearch(value, user.regions);
            handleInputChangeCallAPI(event, value, lastPromise);
          }} // this one being called whenever user inputs a letter
          onChange={handleSearchChange} // this one being called after user click the value in the dropdown
          noOptionsText={'No search result'}
          loadingText={returnLoadingText(emptySearchBar)}
          disabled={rateLimitAlert}
          ListboxProps={{ style: isMobileScreen ? { maxHeight: '200px' } : {} }}
          disableCloseOnSelect={true}
          getOptionLabel={option => {
            return option.searchName;
          }}
          renderOption={(props, option) =>
            option.isDefaultOption ? (
              <li {...props}>
                <Typography>
                  <div className={classes.fixedOption}>
                    <Typography>
                      <IconButton
                        style={{
                          paddingTop: 3,
                          paddingBottom: 3,
                          paddingLeft: 0,
                        }}
                      >
                        <PinDropIcon
                          style={{
                            fontSize: 40,
                            display: 'inline-block',
                            marginBottom: '3px',
                          }}
                        />
                      </IconButton>
                      Draw your search area
                    </Typography>
                  </div>
                </Typography>
              </li>
            ) : (
              <li {...props}>
                <Typography>
                  {option.isSavedRegion && (
                    <IconButton
                      style={{
                        paddingTop: 3,
                        paddingBottom: 3,
                        paddingLeft: 0,
                      }}
                    >
                      <HistoryIcon
                        style={{
                          display: 'inline-block',
                          marginBottom: '3px',
                        }}
                      />
                    </IconButton>
                  )}
                  {capitalizeName(option.searchName)}
                  {option.isVerified && <VerifiedTag size="small" />}
                </Typography>
              </li>
            )
          }
          renderInput={params => (
            <TextField
              {...params}
              inputRef={inputRef}
              placeholder={placeholderText}
              onFocus={() => {
                removePlaceholderText();
                setIsSearchBarActive(true);
              }}
              onBlur={() => {
                restorePlaceholderText();
                setIsSearchBarActive(false);
              }}
              // sx={{
              //   '& .MuiInputLabel-root.Mui-focused': {
              //     // Focused state
              //     display: 'none', // Hide floating label
              //   },
              //   '& .MuiInputBase-input': {
              //     // Style the input itself
              //     paddingTop: 10,
              //     paddingBottom: 10, // Adjust as needed
              //   },
              // }}
              InputLabelProps={{
                shrink: false,
                sx: {
                  color: COLOR_TEXT_3,
                },
              }}
              variant="outlined"
              size={isOnboardingOn ? 'medium' : 'small'}
              InputProps={{
                ...params.InputProps,
                sx: {
                  color: COLOR_TEXT_4,
                  backgroundColor: COLOR_BACKGROUND_2,
                  borderRadius: 200,
                  '& .MuiOutlinedInput-input': {
                    margin: '0px 5px',
                    marginRight: '8px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none !important',
                    boxShadow: '0px 2px 12px -6px rgba(0, 0, 0, 0.4)',
                  },
                  input: {
                    '&::placeholder': {
                      color: COLOR_GREY_600,
                      fontWeight: 500,
                      opacity: 1,
                    },
                  },                  
                },
                startAdornment: <StartAdorement />,
                endAdornment: <EndAdorement />,
              }}
            />
          )}
        />
      </SearchTooltip>
    </>
  );
};

const capitalizeName = (name: string): string => {
  let nameArray: string[] = name.split(' ');
  let capitalizedName: string = '';
  nameArray.forEach(item => {
    capitalizedName += item.charAt(0).toUpperCase() + item.slice(1) + ' ';
  });
  return capitalizedName;
};

export default Asynchronous;
